import React, { useState, useEffect, useCallback } from 'react';
import { publicFetch } from '../utils/publicApi';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function PublicGameRoster({ token, weekOffset }) {
  const [games, setGames] = useState([]);
  const [totalPay, setTotalPay] = useState(0);
  const [attendedPay, setAttendedPay] = useState(0);
  const [cutoffHours, setCutoffHours] = useState(24);
  const [responseEnabled, setResponseEnabled] = useState(true);
  const [payVisible, setPayVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const getDateRange = useCallback(() => {
    const today = new Date();
    const day = today.getDay(); // 0=Sun, 1=Mon, ...
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday + (weekOffset * 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      startDate: formatLocalDate(monday),
      endDate: formatLocalDate(sunday)
    };
  }, [weekOffset]);

  useEffect(() => {
    const loadGames = async () => {
      try {
        setLoading(true);
        setError('');
        const { startDate, endDate } = getDateRange();
        const res = await publicFetch(`${API_BASE}/public/roster/games/${token}?startDate=${startDate}&endDate=${endDate}`);
        if (res.ok) {
          const data = await res.json();
          setGames(data.games || []);
          setTotalPay(data.totalPay || 0);
          setAttendedPay(data.attendedPay || 0);
          if (data.cutoffHours !== undefined) setCutoffHours(data.cutoffHours);
          if (data.responseEnabled !== undefined) setResponseEnabled(data.responseEnabled);
          if (data.payVisible !== undefined) setPayVisible(data.payVisible);
        } else {
          setError('Failed to load game roster');
        }
      } catch (err) {
        setError('Error loading game roster: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, [token, weekOffset, getDateRange]);

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const isWithinCutoff = (game) => {
    if (!game.scheduledTime) return false;
    const gameTime = new Date(game.scheduledTime);
    const cutoffTime = new Date(gameTime.getTime() - cutoffHours * 60 * 60 * 1000);
    return new Date() >= cutoffTime;
  };

  const canRespond = (game) => {
    if (game.isLocked) return false;
    if (game.isPast) return false;
    if (isWithinCutoff(game)) return false;
    return true;
  };

  const handleRespond = async (game, response) => {
    if (response === 'rejected') {
      if (!window.confirm('Are you sure you want to reject this shift?')) return;
    }
    try {
      const res = await publicFetch(`${API_BASE}/public/roster/games/${game.gameId}/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response })
      });
      if (res.ok) {
        setGames(prev => prev.map(g =>
          g.gameId === game.gameId ? { ...g, response } : g
        ));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit response');
      }
    } catch (err) {
      alert('Error submitting response: ' + err.message);
    }
  };

  const getRowClass = (game) => {
    if (game.response === 'rejected') return 'public-rejected-row';
    if (game.response === 'accepted') return 'public-accepted-row';
    if (game.isPast && game.isLocked && !game.attended) return 'public-unattended-row';
    return '';
  };

  // Group games by date
  const groupedByDate = {};
  games.forEach(game => {
    if (!groupedByDate[game.date]) groupedByDate[game.date] = [];
    groupedByDate[game.date].push(game);
  });
  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <div>
      {error && <div className="public-tab-error">{error}</div>}

      {loading ? (
        <div className="public-tab-loading">Loading game roster...</div>
      ) : games.length === 0 ? (
        <div className="public-empty-state">
          <p>No game assignments for this period</p>
        </div>
      ) : (
        <>
          <div className="public-roster-table-scroll">
          <table className="public-roster-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Match</th>
                <th>Division</th>
                <th>Court</th>
                <th>Role</th>
                {payVisible && <th>Pay</th>}
                <th>Status</th>
                {responseEnabled && <th>Response</th>}
              </tr>
            </thead>
            <tbody>
              {sortedDates.map(date => (
                <React.Fragment key={date}>
                  <tr className="public-date-group-header">
                    <td colSpan={responseEnabled ? 8 : 7}>{formatDate(date)}</td>
                  </tr>
                  {groupedByDate[date].map(game => (
                    <tr key={game.gameId} className={getRowClass(game)}>
                      <td>{formatTime(game.time)}</td>
                      <td><strong>{game.team1}</strong> vs <strong>{game.team2}</strong></td>
                      <td>{game.division || '-'}</td>
                      <td>{game.court || '-'}</td>
                      <td>
                        <span className={`public-role-badge ${game.role === 'Referee' ? 'referee' : 'bench-official'}`}>
                          {game.role === 'Referee' && game.refereeCode
                            ? `${game.refereeCode} ${game.role}`
                            : game.role}
                        </span>
                      </td>
                      {payVisible && <td className="public-pay-cell">${game.pay.toFixed(2)}</td>}
                      <td>
                        {game.isPast && game.isLocked && !game.attended ? (
                          <span className="public-status-badge unattended">Unattended</span>
                        ) : game.attended ? (
                          <span className="public-status-badge attended">Attended</span>
                        ) : null}
                      </td>
                      {responseEnabled && <td>
                        {game.response === 'accepted' ? (
                          <span className="public-response-badge accepted">Accepted</span>
                        ) : game.response === 'rejected' ? (
                          <span className="public-response-badge rejected">Rejected</span>
                        ) : (
                          canRespond(game) ? (
                          <div className="public-response-buttons">
                            <button
                              className="public-btn-accept"
                              onClick={() => handleRespond(game, 'accepted')}
                              title="Accept this shift"
                            >&#10003;</button>
                            <button
                              className="public-btn-reject"
                              onClick={() => handleRespond(game, 'rejected')}
                              title="Reject this shift"
                            >&#10007;</button>
                          </div>
                          ) : null
                        )}
                      </td>}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          {payVisible && (
            <div className="public-pay-summary">
              <div className="public-pay-item">
                <span>Forecast Pay:</span>
                <strong>${totalPay.toFixed(2)}</strong>
              </div>
              <div className="public-pay-item attended">
                <span>Attended Pay:</span>
                <strong>${attendedPay.toFixed(2)}</strong>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
